import React, { useState, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text/index';
import { noop } from 'ui-components/default-props/noop';

import { ModalPortal } from '../../components/modal-portal';
import { PickupServiceDropdown } from '../modal-components/pickup-service-dropdown';
import { PickupLocationDropdown } from './components/pickup-location';
import { ShipFromContactDropdown } from './components/shipfrom-contact-dropdown';
import { PickupDate } from './components/pickup-date';
import { PickupWindowTimes } from './components/pickup-window-times';
import { PickupInstructions } from './components/pickup-instructions';
import { AccessCodeSection } from './components/access-code-section';
import { FulfillmentPreview } from '../modal-components/fulfillment-preview/index';
import Global from '../../store/global-selectors';
import { schedulePickup, getLastPickupData, extractErrorCollectionToMessageList } from '../carrier-pickup-api';
import { BadRequestComponent } from '../modal-components/bad-request-component';
import { getSanitizedName } from '../../components/data-driven-logo/name-utils';

import { messages as v1Messages } from '../modal-components/carrier-pickup-modal/messages';
import { messages } from './messages';
import style from './style.module.css';

/* NOTE: V1 modal took selected time and treated it as if it was in UTC
 * e.g. selected warehouse is in Central time (UTC-5), selected time window in UI is 3PM - 6PM
 * Then we'd convert it for the API call as 2025-08-28T15:00:00.000Z - 2025-08-28T18:00:00.000Z
 * Which is 3-6PM _UTC time_, but 10AM - 1PM Central time (UTC-5)
 *
 * However CAPI's intention was "The time relative to the time_zone_iana"
 * https://github.com/shipstation/integrations-shipping/blob/master/carrierapi-common/CarrierApi.Common/Models/Pickup/PickupWindow.cs#L26
 *
 * But of course, in the SS.Business layer right before we send it to CAPI,
 * https://github.com/shipstation/shipstation/blob/master/SS.Business/Shipping/CarrierPickup/CarrierPickupService.cs#L177-L180
 * The record we save to the DB we try to convert the "local" time to UTC again. And that helper function throws if we pass a non UTC timezone (DateTimeKind = local)
 * So ultimately, this SS.Business roadblock means we have to send time in a UTC "timezone" (or timezoneless) but the actual hour/minute represents the local timezone
 *
 * So this is horribly confusing when you're going through the whole stack
 * Also why separate date, time, and timezone request properties when an ISO timestamp can encapsulate date, time and zone...
 *
 * TLDR - V1Modal did that approach intentionally given the way the backend is structured.
 * We'll adhere to that now for compatibility
 * Since we have all these separate properties, the backend just cares about the date component of pickupdate and the time component of time windowclose/open
 */
const formatPickupDateTimes = (requestedDate, requestedTimeStart, requestedTimeEnd, shipFromTimeZone) => {
  // Convert moment.js format to Luxon format: MM/DD/YYYY -> MM/dd/yyyy
  const luxonDateFormat = 'MM/dd/yyyy';
  const pickupDate = DateTime.fromFormat(requestedDate, luxonDateFormat, { zone: shipFromTimeZone }).startOf('day');

  const startTime = DateTime.fromFormat(requestedTimeStart, 'HH:mm:ss', { zone: shipFromTimeZone });
  const endTime = DateTime.fromFormat(requestedTimeEnd, 'HH:mm:ss', { zone: shipFromTimeZone });

  const windowStartTimeString = pickupDate
    .setZone('utc')
    .set({
      hour: startTime.hour,
      minute: startTime.minute,
      second: 0,
    })
    .toISO();

  const windowCloseTimeString = pickupDate
    .setZone('utc')
    .set({
      hour: endTime.hour,
      minute: endTime.minute,
      second: 0,
    })
    .toISO();

  return {
    formattedPickupDate: pickupDate.toISODate(),
    formattedWindowStartTime: windowStartTimeString,
    formattedWindowCloseTime: windowCloseTimeString,
  };
};

const getMinimumDaysAccountingForCutoff = (shipFromTimeZone, minimumDaysInAdvance, maxCutOffSchedulingHourRelative = null) => {
  const now = DateTime.now().setZone(shipFromTimeZone);
  let daysToAdd = minimumDaysInAdvance;

  if (maxCutOffSchedulingHourRelative !== null) {
    const timeObj = DateTime.fromFormat(maxCutOffSchedulingHourRelative, 'HH:mm:ss');
    const cutoffMoment = DateTime.now().setZone(shipFromTimeZone).set({
      hour: timeObj.hour,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    daysToAdd = now > cutoffMoment ? minimumDaysInAdvance + 1 : minimumDaysInAdvance;
  }

  return daysToAdd;
};

const luxonSundayWeekdayInt = 7;
// NOTE: format to a string to play well with DatePicker moment.js
const getDefaultPickupDate = (shipFromTimeZone, daysInAdvance, shouldDisableSundayPickups) => {
  const dateFormatForMoment = 'MM/dd/yyyy';
  const pickupDate = DateTime.now().setZone(shipFromTimeZone).startOf('day').plus({ days: daysInAdvance });

  if (pickupDate.weekday === luxonSundayWeekdayInt && shouldDisableSundayPickups) {
    return pickupDate.plus({ days: 1 }).toFormat(dateFormatForMoment);
  }

  const formattedDate = pickupDate.toFormat(dateFormatForMoment);
  return formattedDate;
};

/**
 * CarrierPickupModalV2 component for scheduling carrier pickups
 * @param {object} props
 * @param {object} [props.bodyNoteMessageDescriptor] - Message descriptor for body note
 * @param {object} [props.footerNoteMessageDescriptor] - Message descriptor for footer note
 * @param {boolean} [props.hasFixedTimeWindow] - Whether the pickup has a fixed time window
 * @param {string} [props.fixedWarehouseTimeStart] - Fixed start time relative to warehouse timezone for pickup window in HH:mm:ss format.
 * @param {string} [props.fixedWarehouseTimeEnd] - Fixed end time relative to warehouse timezone for pickup window in HH:mm:ss format.
 * @param {number|null} [props.minimumDaysInAdvance] - Minimum days in advance for scheduling. 0 means same day is allowed.
 * @param {string|null} [props.maxCutOffSchedulingHourRelative] - Cutoff time in HH:mm:ss format (e.g., "18:00:00").
 *    If selected warehouse time is past this cutoff, minimum days is minimumDaysInAdvance + 1.
 *    If this value is null, a request can be scheduled at any point in the day.
 *    This is relative to the warehouse's time zone.
 * @param {(data: object) => void} [props.onCreated] - Callback when pickup is successfully created
 * @param {() => void} props.onClose - Callback when modal is closed
 * @param {Array} [props.pickupServices] - Available pickup services
 * @param {string} [props.sellerLabelProviderId] - ID of the seller label provider
 * @param {boolean} [props.shouldDisableSundayPickups] - Whether the pickup disallows Sundays
 * @returns {React.ReactElement} The CarrierPickupModalV2 component
 */
export const CarrierPickupModalV2 = ({
  bodyNoteMessageDescriptor = undefined,
  footerNoteMessageDescriptor = undefined,
  hasFixedTimeWindow = false,
  fixedWarehouseTimeStart = '',
  fixedWarehouseTimeEnd = '',
  minimumDaysInAdvance = 0,
  maxCutOffSchedulingHourRelative = null,
  onCreated = noop,
  onClose = noop,
  pickupServices = [],
  sellerLabelProviderId = '',
  shouldDisableSundayPickups = false,
}) => {
  const shipFroms = useSelector(Global.selectSellerShipFroms);
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const labelProviders = useSelector(Global.selectReferenceDatagetProviders);
  const sellerLabelProvider = sellerLabelProviders.find((provider) => provider.sellerLabelProviderId === sellerLabelProviderId);
  const labelProvider = labelProviders.find((provider) => provider.labelProviderId === sellerLabelProvider.labelProviderId);
  const labelProviderName = getSanitizedName(labelProvider.name);

  const [isCreating, setIsCreating] = useState(false);
  const [errorCollection, setErrorCollection] = useState([]);
  const [pickupServiceCode, setPickupServiceCode] = useState(() => {
    if (pickupServices.length === 1) {
      return pickupServices[0].code;
    }
    return null;
  });
  const [shipFromId, setShipFromId] = useState(() => {
    if (shipFroms.length !== 0) {
      const defaultShipFrom = shipFroms.find((shipFrom) => shipFrom.isDefault);
      return defaultShipFrom ? defaultShipFrom.shipFromId : shipFroms[0].shipFromId;
    }
    return null;
  });

  const selectedShipFrom = shipFroms.find((shipFrom) => shipFrom.shipFromId === shipFromId);
  const shipFromTimeZone = selectedShipFrom?.originAddress?.ianaTimeZone;
  const adjustedMinimumDaysInAdvance = getMinimumDaysAccountingForCutoff(
    shipFromTimeZone,
    minimumDaysInAdvance,
    maxCutOffSchedulingHourRelative,
  );

  const [shipFromContactId, setShipFromContactId] = useState(null);
  const [selectedPickupDate, setSelectedPickupDate] = useState(() => {
    return shipFromTimeZone ? getDefaultPickupDate(shipFromTimeZone, adjustedMinimumDaysInAdvance, shouldDisableSundayPickups) : '';
  });
  const [selectedTimeStart, setSelectedTimeStart] = useState(hasFixedTimeWindow ? fixedWarehouseTimeStart : '');
  const [selectedTimeEnd, setSelectedTimeEnd] = useState(hasFixedTimeWindow ? fixedWarehouseTimeEnd : '');
  const [pickupInstructions, setPickupInstructions] = useState('');
  const [needsAccessCode, setNeedsAccessCode] = useState(false);
  const [accessCode, setAccessCode] = useState('');

  useEffect(() => {
    if (!shipFromTimeZone) {
      return;
    }

    const newDefaultDate = getDefaultPickupDate(shipFromTimeZone, adjustedMinimumDaysInAdvance, shouldDisableSundayPickups);
    setSelectedPickupDate(newDefaultDate);
  }, [adjustedMinimumDaysInAdvance, shipFromTimeZone, shouldDisableSundayPickups]);

  useEffect(() => {
    if (!shipFromId) {
      return;
    }

    getLastPickupData(shipFromId).then(({ pickupInstructions: lastPickupInstructions = '', accessCode: lastAccessCode = '' }) => {
      setPickupInstructions(lastPickupInstructions);
      setNeedsAccessCode(!!lastAccessCode);
      setAccessCode(lastAccessCode);
    });
  }, [shipFromId]);

  const canSchedule = shipFromId && shipFromContactId && selectedPickupDate && selectedTimeStart && selectedTimeEnd;

  const onModalClose = useCallback(() => {
    if (isCreating) {
      return;
    }
    onClose();
  }, [onClose, isCreating]);

  const submit = () => {
    setIsCreating(true);

    const { formattedPickupDate, formattedWindowStartTime, formattedWindowCloseTime } = formatPickupDateTimes(
      selectedPickupDate,
      selectedTimeStart,
      selectedTimeEnd,
      shipFromTimeZone,
    );

    const request = {
      sellerProviderId: sellerLabelProviderId,
      shipFromId,
      fulfillmentIds: [],
      shipFromContactId,
      pickupServiceCode: null,
      pickupDate: formattedPickupDate,
      pickupWindowOpen: formattedWindowStartTime,
      pickupWindowClose: formattedWindowCloseTime,
      pickupInstructions: pickupInstructions || undefined,
      accessCode: needsAccessCode ? accessCode : undefined,
      timeZoneIana: shipFromTimeZone,
      shouldUseDateRangeFromToday: true,
    };

    schedulePickup(request)
      .then(({ data }) => {
        onCreated(data);
      })
      .catch(({ response }) => {
        setErrorCollection(extractErrorCollectionToMessageList(response, []));
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  return (
    <ModalPortal onClose={onModalClose}>
      <Modal className={style.modal} size='large'>
        <ModalHeader closeModal={onModalClose}>
          <FormattedMessage
            {...v1Messages.carrierPickUpModalHeader}
            values={{
              labelProviderName,
              sellerLabelProviderName: sellerLabelProvider.name,
            }}
          />
        </ModalHeader>
        <ModalBody className={style.modalBody}>
          <div className={style.mainContent}>
            <BadRequestComponent messages={errorCollection} />
            {pickupServices && pickupServices.length > 1 && (
              <PickupServiceDropdown
                onPickupServiceSelected={setPickupServiceCode}
                pickupServices={pickupServices}
                selectedPickupServiceCode={pickupServiceCode}
              />
            )}
            <PickupLocationDropdown onShipFromSelected={setShipFromId} selectedShipFromId={shipFromId} shipFroms={shipFroms} />
            <ShipFromContactDropdown
              onContactSelected={setShipFromContactId}
              selectedContactId={shipFromContactId}
              shipFromId={shipFromId}
            />
            <PickupDate
              // Note: ui-components DatePicker maintains internal state so we need a key to reset when timezone/minDate changes
              key={`${shipFromTimeZone}-${selectedPickupDate}`}
              minimumDaysInAdvance={adjustedMinimumDaysInAdvance}
              pickupDate={selectedPickupDate}
              setPickupDate={setSelectedPickupDate}
              shouldDisableSundayPickups={shouldDisableSundayPickups}
              timeZoneIana={shipFromTimeZone}
            />
            <PickupWindowTimes
              hasFixedTimeWindow={hasFixedTimeWindow}
              onEndTimeChange={setSelectedTimeEnd}
              onStartTimeChange={setSelectedTimeStart}
              pickupWindowEndTime={selectedTimeEnd}
              pickupWindowStartTime={selectedTimeStart}
              timeZoneIana={shipFromTimeZone}
            />
            <PickupInstructions onChange={setPickupInstructions} value={pickupInstructions} />
            <AccessCodeSection
              accessCode={accessCode}
              needsAccessCode={needsAccessCode}
              onAccessCodeChange={setAccessCode}
              onNeedsAccessCodeChange={setNeedsAccessCode}
            />
            {bodyNoteMessageDescriptor && (
              <div className={style.bodyNote}>
                <Text textType={TextType.Paragraph} value={<FormattedMessage {...bodyNoteMessageDescriptor} />} />
              </div>
            )}
          </div>
          <div className={style.sidebar}>
            <FulfillmentPreview
              pickupDate={selectedPickupDate}
              pickupServiceCode={pickupServiceCode}
              sellerLabelProviderId={sellerLabelProviderId}
              shipFromId={shipFromId}
              shouldUseDateRangeFromToday
              timeZoneIana={shipFromTimeZone}
            />
          </div>
        </ModalBody>
        <ModalFooter className={style.footer}>
          <div>
            {footerNoteMessageDescriptor && (
              <Text
                className={style.footerNote}
                textType={TextType.Paragraph}
                value={<FormattedMessage {...footerNoteMessageDescriptor} />}
              />
            )}
          </div>
          <div className={style.buttons}>
            <Button disabled={isCreating} onClick={onClose} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.cancelButton} />
            </Button>
            <Button disabled={isCreating || !canSchedule} onClick={submit} theme={THEME_CONSTANTS.primary}>
              <FormattedMessage {...messages.schedulePickupButton} />
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
